import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { auth, db, functions as firebaseFunctions, rtdb } from '../firebase/firebase.js';
import { httpsCallable } from 'firebase/functions';
import { onAuthStateChanged, signOut, signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { ref, onValue, set, onDisconnect, serverTimestamp } from "firebase/database";

// --- Create Context ---
const AuthContext = React.createContext();

// --- Custom Hook ---
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
}

// --- All Firebase Callable Functions ---
const completeRegistrationCallable = httpsCallable(firebaseFunctions, 'completeRegistration');
const checkUserStatusCallable = httpsCallable(firebaseFunctions, 'checkUserStatus');
const tempSetMyRoleCallable = httpsCallable(firebaseFunctions, 'temp_setMyRole');
const toggleStaffApplicationsCallable = httpsCallable(firebaseFunctions, 'toggleStaffApplications');
const submitOnlineApplicationCallable = httpsCallable(firebaseFunctions, 'submitOnlineApplication');
const approveStaffApplicationCallable = httpsCallable(firebaseFunctions, 'approveStaffApplication');
const appointStaffCallable = httpsCallable(firebaseFunctions, 'appointStaff');
const createQuestionPaperCallable = httpsCallable(firebaseFunctions, 'createQuestionPaper');
const createApplicationFormCallable = httpsCallable(firebaseFunctions, 'createApplicationForm');
const publishExamAdvertisementCallable = httpsCallable(firebaseFunctions, 'publishExamAdvertisement');
const fetchStudentDynamicFormCallable = httpsCallable(firebaseFunctions, 'fetchStudentDynamicForm');
const addQuestionToPaperCallable = httpsCallable(firebaseFunctions, 'addQuestionToPaper');
const finalizePDFUploadCallable = httpsCallable(firebaseFunctions, 'finalizePDFUpload');
const manageBulkQuestionsCallable = httpsCallable(firebaseFunctions, 'manageBulkQuestions');
const scheduleExamRegistrationCallable = httpsCallable(firebaseFunctions, 'scheduleExamRegistration');
const fetchScheduledExamsCallable = httpsCallable(firebaseFunctions, 'fetchScheduledExams');
const startExamSessionCallable = httpsCallable(firebaseFunctions, 'startExamSession');
const finalizeStudentSubmissionCallable = httpsCallable(firebaseFunctions, 'finalizeStudentSubmission');
const fetchCoursesAndLiveClassesCallable = httpsCallable(firebaseFunctions, 'fetchCoursesAndLiveClasses');
const registerCoursePurchaseCallable = httpsCallable(firebaseFunctions, 'registerCoursePurchase');
const fetchStudentFinalResultCallable = httpsCallable(firebaseFunctions, 'fetchStudentFinalResult');
const fetchSubmissionsForGradingCallable = httpsCallable(firebaseFunctions, 'fetchSubmissionsForGrading');
const finalizeGradeAndDecrementLoadCallable = httpsCallable(firebaseFunctions, 'finalizeGradeAndDecrementLoad');
const assignAllPendingSubmissionsCallable = httpsCallable(firebaseFunctions, 'assignAllPendingSubmissions');
const manageExaminerCallable = httpsCallable(firebaseFunctions, 'manageExaminer');
const fetchWalletDataCallable = httpsCallable(firebaseFunctions, 'fetchWalletData');
const initiateRechargeCallable = httpsCallable(firebaseFunctions, 'initiateRecharge');
const processPaymentCallbackCallable = httpsCallable(firebaseFunctions, 'processPaymentCallback');
const purchaseServiceWithCoinsCallable = httpsCallable(firebaseFunctions, 'purchaseServiceWithCoins');
const applyCouponCallable = httpsCallable(firebaseFunctions, 'applyCoupon');
const redeemGiftCardCallable = httpsCallable(firebaseFunctions, 'redeemGiftCard');
const createLiveClassSessionCallable = httpsCallable(firebaseFunctions, 'createLiveClassSession');
const createEventCallable = httpsCallable(firebaseFunctions, 'createEvent');
const updateEventCallable = httpsCallable(firebaseFunctions, 'updateEvent');
const fetchEventsForUserCallable = httpsCallable(firebaseFunctions, 'fetchEventsForUser');
const registerForEventCallable = httpsCallable(firebaseFunctions, 'registerForEvent');
const createPaymentOrderCallable = httpsCallable(firebaseFunctions, 'createPaymentOrder');
const checkInParticipantCallable = httpsCallable(firebaseFunctions, 'checkInParticipant');
const sendStaffInviteCallable = httpsCallable(firebaseFunctions, 'sendStaffInvite');
const uploadEventResultsCallable = httpsCallable(firebaseFunctions, 'uploadEventResults');
const generateCertificateCallable = httpsCallable(firebaseFunctions, 'generateCertificate');
const fetchPublicPortalDataCallable = httpsCallable(firebaseFunctions, 'fetchPublicPortalData');
const updateUserStatusCallable = httpsCallable(firebaseFunctions, 'updateUserStatus');
const deleteUserAccountCallable = httpsCallable(firebaseFunctions, 'deleteUserAccount');
const checkProfileUniquenessCallable = httpsCallable(firebaseFunctions, 'checkProfileUniqueness');

// --- Auth Provider ---
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [isAcceptingStaff, setIsAcceptingStaff] = useState(false);
    const [profileError, setProfileError] = useState(null);

    // --- Generic callable function handler ---
    const callFunction = useCallback(async (callable, data = {}, requiredRole) => {
        const userRole = userProfile?.role;
        if (requiredRole && userRole !== requiredRole && userRole !== 'owner') {
            const error = new Error(`Permission Denied: ${requiredRole} required`);
            error.code = 'permission-denied';
            throw error;
        }
        try {
            const result = await callable(data);
            return result.data;
        } catch (error) {
            const enrichedError = new Error(error.message || "Error calling function");
            enrichedError.code = error.code;
            enrichedError.details = error.details;
            throw enrichedError;
        }
    }, [userProfile]);

    // --- Management Wrappers ---
    const updateUserStatus = useCallback((userId, isEnabled) => callFunction(updateUserStatusCallable, { userId, isEnabled }, 'owner'), [callFunction]);
    const deleteUserAccount = useCallback((userId) => callFunction(deleteUserAccountCallable, { userId }, 'owner'), [callFunction]);

    // --- Core Callable Wrappers ---
    const completeRegistration = useCallback((email, password, verifiedData) =>
        callFunction(completeRegistrationCallable, { password, verifiedData: { ...verifiedData, email } }), [callFunction]);
    const checkUserStatus = useCallback((email) => callFunction(checkUserStatusCallable, { email }), [callFunction]);
    const TEMPORARY_RUN_SET_ROLE = useCallback(() => callFunction(tempSetMyRoleCallable, {}), [callFunction]);
    const toggleStaffApplications = useCallback((isAccepting) => callFunction(toggleStaffApplicationsCallable, { isAccepting }, 'owner'), [callFunction]);
    const submitPublicApplication = useCallback((data) => callFunction(submitOnlineApplicationCallable, data), [callFunction]);
    const approveStaffApplication = useCallback((applicationId) => callFunction(approveStaffApplicationCallable, { applicationId }, 'owner'), [callFunction]);
    const appointStaff = useCallback((staffData) => callFunction(appointStaffCallable, staffData, 'owner'), [callFunction]);
    const createQuestionPaper = useCallback((paperData) => callFunction(createQuestionPaperCallable, paperData, 'exam_admin'), [callFunction]);
    const createApplicationForm = useCallback((formData) => callFunction(createApplicationFormCallable, formData, 'exam_admin'), [callFunction]);
    const publishExamAdvertisement = useCallback((advData) => callFunction(publishExamAdvertisementCallable, advData, 'exam_admin'), [callFunction]);
    const fetchStudentDynamicForm = useCallback((advertId) => callFunction(fetchStudentDynamicFormCallable, { advertId }), [callFunction]);
    const addQuestionToPaper = useCallback((payload) => callFunction(addQuestionToPaperCallable, payload, 'educator'), [callFunction]);
    const finalizePDFUpload = useCallback((payload) => callFunction(finalizePDFUploadCallable, payload, 'educator'), [callFunction]);
    const manageBulkQuestions = useCallback((payload) => callFunction(manageBulkQuestionsCallable, payload, 'educator'), [callFunction]);
    const scheduleExamRegistration = useCallback((details) => callFunction(scheduleExamRegistrationCallable, details, 'exam_admin'), [callFunction]);
    const fetchScheduledExams = useCallback(async () => { const result = await callFunction(fetchScheduledExamsCallable, {}); return result?.exams ?? []; }, [callFunction]);
    const startExamSession = useCallback((examDetails) => callFunction(startExamSessionCallable, examDetails), [callFunction]);
    const finalizeStudentSubmission = useCallback((submissionPayload) => callFunction(finalizeStudentSubmissionCallable, submissionPayload), [callFunction]);
    const fetchCoursesAndLiveClasses = useCallback(async () => { const result = await callFunction(fetchCoursesAndLiveClassesCallable, {}); return result?.courses ?? []; }, [callFunction]);
    const registerCoursePurchase = useCallback((purchaseDetails) => callFunction(registerCoursePurchaseCallable, purchaseDetails), [callFunction]);
    const fetchStudentFinalResult = useCallback((paperId) => callFunction(fetchStudentFinalResultCallable, { paperId }), [callFunction]);
    const fetchSubmissionsForGrading = useCallback(() => callFunction(fetchSubmissionsForGradingCallable, {}, 'examiner'), [callFunction]);
    const finalizeGradeAndDecrementLoad = useCallback((gradeDetails) => callFunction(finalizeGradeAndDecrementLoadCallable, gradeDetails, 'examiner'), [callFunction]);
    const assignAllPendingSubmissions = useCallback(() => callFunction(assignAllPendingSubmissionsCallable, {}, 'exam_admin'), [callFunction]);
    const manageExaminer = useCallback((details) => callFunction(manageExaminerCallable, details, 'admin'), [callFunction]);
    const fetchWalletData = useCallback(() => callFunction(fetchWalletDataCallable, {}), [callFunction]);
    const initiateRecharge = useCallback((amount) => callFunction(initiateRechargeCallable, { amount }), [callFunction]);
    const processPaymentCallback = useCallback((details) => callFunction(processPaymentCallbackCallable, details), [callFunction]);
    const purchaseServiceWithCoins = useCallback((details) => callFunction(purchaseServiceWithCoinsCallable, details), [callFunction]);
    const applyCoupon = useCallback((coupon, amount) => callFunction(applyCouponCallable, { couponCode: coupon, amount }), [callFunction]);
    const redeemGiftCard = useCallback((cardCode) => callFunction(redeemGiftCardCallable, { cardCode }), [callFunction]);
    const createLiveClassSession = useCallback((details) => callFunction(createLiveClassSessionCallable, details, 'educator'), [callFunction]);
    const createEvent = useCallback((details) => callFunction(createEventCallable, details, 'event_admin'), [callFunction]);
    const updateEvent = useCallback((id, data) => callFunction(updateEventCallable, { eventId: id, ...data }, 'event_admin'), [callFunction]);
    const fetchEventsForUser = useCallback((role) => callFunction(fetchEventsForUserCallable, { role }), [callFunction]);
    const registerForEvent = useCallback((details) => callFunction(registerForEventCallable, details), [callFunction]);
    const createPaymentOrder = useCallback((data) => callFunction(createPaymentOrderCallable, data), [callFunction]);
    const checkInParticipant = useCallback((details) => callFunction(checkInParticipantCallable, details, 'event_staff'), [callFunction]);
    const sendStaffInvite = useCallback((details) => callFunction(sendStaffInviteCallable, details, 'event_admin'), [callFunction]);
    const uploadEventResults = useCallback((payload) => callFunction(uploadEventResultsCallable, payload, 'event_admin'), [callFunction]);
    const generateCertificate = useCallback((resultId) => callFunction(generateCertificateCallable, { resultId }), [callFunction]);
    const fetchPublicPortalData = useCallback(() => callFunction(fetchPublicPortalDataCallable, {}), [callFunction]);

    // --- Update Student Profile ---
    const updateStudentProfile = useCallback(async (uid, profileData) => {
        if (!uid || uid !== userProfile?.uid) throw new Error("User ID mismatch.");
        const userDocRef = doc(db, 'users', uid);
        const phoneToSave = profileData.phone ? String(profileData.phone).trim() : null;
        try {
            if (phoneToSave && phoneToSave !== userProfile?.phone) {
                await callFunction(checkProfileUniquenessCallable, { phone: phoneToSave });
            }
            await updateDoc(userDocRef, { ...profileData, phone: phoneToSave, profileComplete: true, updatedAt: new Date() });
            return { success: true, message: "Profile updated!" };
        } catch (error) {
            throw new Error(error.message || "Failed to update profile.");
        }
    }, [userProfile, callFunction]);

    // --- Auth Functions ---
    const login = useCallback((email, password) => signInWithEmailAndPassword(auth, email, password), []);
    const logout = useCallback(async () => { try { await signOut(auth); } catch {} }, []);
    const signInWithGoogle = useCallback(async () => {
        const provider = new GoogleAuthProvider();
        return await signInWithPopup(auth, provider);
    }, []);

    // --- Auth State Listener ---
    useEffect(() => {
        let isMounted = true;
        let profileUnsub = null;

        const unsubscribeAuth = onAuthStateChanged(auth, (currentUserAuth) => {
            if (!isMounted) return;
            setUser(currentUserAuth);

            if (profileUnsub) profileUnsub();

            if (currentUserAuth) {
                setLoading(true);
                const userDocRef = doc(db, 'users', currentUserAuth.uid);
                profileUnsub = onSnapshot(userDocRef, (docSnap) => {
                    if (!isMounted) return;
                    if (docSnap.exists()) setUserProfile({ uid: currentUserAuth.uid, email: currentUserAuth.email, ...docSnap.data() });
                    else setUserProfile(null);
                    setLoading(false);
                }, (error) => { console.error(error); setLoading(false); });
            } else {
                setUserProfile(null);
                setLoading(false);
            }
        });

        return () => { isMounted = false; unsubscribeAuth(); if (profileUnsub) profileUnsub(); };
    }, []);

    // --- Staff Presence Effect ---
    useEffect(() => {
        if (!userProfile?.uid) return;
        const isStaff = userProfile.role && userProfile.role !== 'student' && userProfile.role !== 'guest';
        if (!isStaff) return;

        const userStatusRef = ref(rtdb, '/status/' + userProfile.uid);
        const amOnline = ref(rtdb, '.info/connected');

        const unsubscribe = onValue(amOnline, (snap) => {
            if (snap.val() === true) {
                set(userStatusRef, { online: true, last_changed: serverTimestamp() });
                onDisconnect(userStatusRef).set({ online: false, last_changed: serverTimestamp() });
            }
        });

        return () => { unsubscribe(); set(userStatusRef, { online: false, last_changed: serverTimestamp() }); };
    }, [userProfile]);

    // --- Context Value ---
    const value = useMemo(() => ({
        user, userProfile, loading, settingsLoading, profileError, isAcceptingStaff,
        login, logout, signInWithGoogle, updateStudentProfile,
        updateUserStatus, deleteUserAccount,
        completeRegistration, checkUserStatus, TEMPORARY_RUN_SET_ROLE,
        toggleStaffApplications, submitPublicApplication, approveStaffApplication, appointStaff,
        createQuestionPaper, createApplicationForm, publishExamAdvertisement, fetchStudentDynamicForm,
        addQuestionToPaper, finalizePDFUpload, manageBulkQuestions,
        scheduleExamRegistration, fetchScheduledExams, startExamSession, finalizeStudentSubmission,
        fetchCoursesAndLiveClasses, registerCoursePurchase, fetchStudentFinalResult,
        fetchSubmissionsForGrading, finalizeGradeAndDecrementLoad, assignAllPendingSubmissions,
        manageExaminer, fetchWalletData, initiateRecharge, processPaymentCallback, purchaseServiceWithCoins,
        applyCoupon, redeemGiftCard, createLiveClassSession, createEvent, updateEvent,
        fetchEventsForUser, registerForEvent, createPaymentOrder, checkInParticipant, sendStaffInvite,
        uploadEventResults, generateCertificate, fetchPublicPortalData
    }), [user, userProfile, loading, settingsLoading, profileError, isAcceptingStaff]);

    return (
        <AuthContext.Provider value={value}>
            {loading || settingsLoading ? (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <p className="text-lg font-semibold text-indigo-600 animate-pulse">Initializing Portal...</p>
                </div>
            ) : children}
        </AuthContext.Provider>
    );
}
